<script lang="ts">
	import ArrowLeftRight from '@lucide/svelte/icons/arrow-left-right'
	import { cn } from 'tailwind-variants'

	import { goto } from '$app/navigation'
	import { resolve } from '$app/paths'
	import { page } from '$app/state'
	import ComboBox from '#lib/components/combo-box.svelte'
	import ComparisonItem from '#lib/components/comparison-item.svelte'
	import CtaButton from '#lib/components/cta-button.svelte'
	import OpenInAi from '#lib/components/open-in-ai.svelte'
	import { Button } from '#lib/components/ui/button/index.js'
	import * as Card from '#lib/components/ui/card/index.js'
	import { CHAINS_CONFIG, LISTED_CHAINS } from '#lib/constants/chainsConfig.js'
	import type { ChainId } from '#lib/models/index.js'
	import { phg } from '#lib/utils/posthog.js'

	let { data, children } = $props()

	let leftSelect = $derived(data.left?.id)
	let rightSelect = $derived(data.right?.id)

	const gotoComparison = (leftId: Nullable<ChainId>, rightId: Nullable<ChainId>) => {
		const [leftSlug, rightSlug] = [
			LISTED_CHAINS.find(({ id }) => id === leftId)?.slug,
			LISTED_CHAINS.find(({ id }) => id === rightId)?.slug,
		]

		if (!leftSlug || !rightSlug) return

		const slug = `${leftSlug}-vs-${rightSlug}`

		if (slug !== page.params.slug) {
			goto(resolve('/(app)/compare/[slug]', { slug }))

			phg.capture('compare_chains', { slug })
		}
	}

	const POPULAR_COMPARISONS = [
		{ left: CHAINS_CONFIG.keeta_mainnet_chain, right: CHAINS_CONFIG.solana_chain },
		{ left: CHAINS_CONFIG.ethereum_chain, right: CHAINS_CONFIG.solana_chain },
		{ left: CHAINS_CONFIG.icp_chain, right: CHAINS_CONFIG.solana_chain },
		{ left: CHAINS_CONFIG.somnia_chain, right: CHAINS_CONFIG.solana_chain },
		{ left: CHAINS_CONFIG.sui_chain, right: CHAINS_CONFIG.solana_chain },
		{ left: CHAINS_CONFIG.bnb_chain, right: CHAINS_CONFIG.solana_chain },
		{ left: CHAINS_CONFIG.aptos_chain, right: CHAINS_CONFIG.sui_chain },
		{ left: CHAINS_CONFIG.aptos_chain, right: CHAINS_CONFIG.arbitrum_chain },
		{ left: CHAINS_CONFIG.aptos_chain, right: CHAINS_CONFIG.sei_chain },
		{ left: CHAINS_CONFIG.sonic_chain, right: CHAINS_CONFIG.solana_chain },
		{ left: CHAINS_CONFIG.somnia_chain, right: CHAINS_CONFIG.sui_chain },
		{ left: CHAINS_CONFIG.bitcoin_chain, right: CHAINS_CONFIG.ethereum_chain },
	]
</script>

<section class="container max-w-245 px-3 py-4 sm:p-6">
	<CtaButton class="mb-6 w-full font-extrabold sm:mb-8" size="xl" />

	<div
		class={cn('mb-6 sm:mb-10', {
			'flex flex-col items-center justify-between gap-4 sm:flex-row': data.left && data.right,
		})}
	>
		<h1
			class={cn('pr-0.5 text-center text-3xl font-extrabold md:text-4xl lg:text-4xl', {
				'sm:text-left': data.left && data.right,
			})}
		>
			{#if data.left && data.right}
				Compare {data.left.name} vs {data.right.name}
			{:else}
				Blockchain Comparison
			{/if}
		</h1>

		{#if data.left && data.right}
			<OpenInAi
				class="w-full sm:w-auto"
				prompt="I’m comparing {data.left.name} vs {data.right
					.name} on Chainspect: https://chainspect.app/compare/{data.left.slug}-vs-{data.right
					.slug}. Based on the data, can you briefly summarize how they stack up?"
			/>
		{/if}
	</div>

	<div class="mb-10 flex flex-col items-stretch justify-between gap-2 sm:flex-row sm:items-center">
		<ComboBox
			class="flex-1"
			onChange={() => gotoComparison(leftSelect, rightSelect)}
			options={LISTED_CHAINS.map(({ name, id, slug }) => ({
				label: name,
				value: id,
				slug,
				disabled: id === rightSelect,
			}))}
			placeholder="Select First Blockchain"
			bind:value={leftSelect}
		/>

		<Button
			class="mx-auto aspect-square"
			aria-label="Swap Blockchains"
			onclick={() => gotoComparison(rightSelect, leftSelect)}
			size="icon-lg"
			variant="ghost"
		>
			<ArrowLeftRight class="rotate-90 sm:rotate-0" size="20" />
		</Button>

		<ComboBox
			class="flex-1"
			onChange={() => gotoComparison(leftSelect, rightSelect)}
			options={LISTED_CHAINS.map(({ name, id, slug }) => ({
				label: name,
				value: id,
				slug,
				disabled: id === leftSelect,
			}))}
			placeholder="Select Second Blockchain"
			bind:value={rightSelect}
		/>
	</div>

	{@render children?.()}

	{#if !(data.left && data.right)}
		<h2 class="mb-4 text-xl">Popular Blockchain Comparisons</h2>

		<div
			class="bg-border mb-7 grid w-full grid-cols-1 gap-px overflow-hidden rounded-xl border shadow lg:grid-cols-2"
		>
			{#each POPULAR_COMPARISONS as { left, right } (left.slug + right.slug)}
				<ComparisonItem {left} {right} type="blockchains" />
			{/each}
		</div>

		<Card.Root>
			<Card.Header>
				<Card.Title class="text-xl" tag="h2">How to Compare Blockchain Networks</Card.Title>
			</Card.Header>

			<Card.Content class="prose">
				<p>
					A useful blockchain comparison should rely on consistent, verifiable data rather than
					headline claims. Chainspect lets you compare any two blockchain platforms side by side
					using current network data.
				</p>

				<p>
					Select two blockchains above to see where each network leads and measure the difference.
				</p>
			</Card.Content>
		</Card.Root>
	{/if}
</section>
